import React, {createContext, useContext, useReducer, ReactNode} from "react";

interface CartItem {
  id: number;
  title: string;
  price: number;
  quantity: number;
  cover_url?: string;
  available_quantity: number;
  status?: string;
  release_date?: string | null;
}

interface CartState {
  items: CartItem[];
  total: number;
}

type CartAction =
  | {type: "ADD_ITEM"; payload: CartItem}
  | {type: "REMOVE_ITEM"; payload: number}
  | {type: "UPDATE_QUANTITY"; payload: {id: number; quantity: number}}
  | {type: "CLEAR_CART"};

const CartContext = createContext<
  | {
      state: CartState;
      dispatch: React.Dispatch<CartAction>;
    }
  | undefined
>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case "ADD_ITEM": {
      const existingItem = state.items.find(
        (item) => item.id === action.payload.id
      );

      if (existingItem) {
        return {
          ...state,
          items: state.items.map((item) =>
            item.id === action.payload.id
              ? {...item, quantity: item.quantity + action.payload.quantity}
              : item
          ),
          total: state.total + action.payload.price * action.payload.quantity,
        };
      } else {
        return {
          ...state,
          items: [...state.items, action.payload],
          total: state.total + action.payload.price * action.payload.quantity,
        };
      }
    }

    case "REMOVE_ITEM": {
      const item = state.items.find((item) => item.id === action.payload);
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload),
        total: state.total - (item ? item.price * item.quantity : 0),
      };
    }

    case "UPDATE_QUANTITY": {
      const item = state.items.find((item) => item.id === action.payload.id);
      if (!item) return state;

      const quantityDiff = action.payload.quantity - item.quantity;

      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id
            ? {...item, quantity: action.payload.quantity}
            : item
        ),
        total: state.total + item.price * quantityDiff,
      };
    }

    case "CLEAR_CART":
      return {
        items: [],
        total: 0,
      };

    default:
      return state;
  }
};

export const CartProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    total: 0,
  });

  return (
    <CartContext.Provider value={{state, dispatch}}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

// Export CartContext for testing
export {CartContext};
